import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ErrorModal,
  LoadingIndicator,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import {
  DIRECT_TO_HOME,
  FUSION_CLOSER,
  FUSION_SETTER,
  getSplatUserType,
} from "@sunrun/sales-experience-shared";
// eslint-disable-next-line import/no-restricted-paths
import { PricingPage } from "components/pages/PricingPage";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { useAppSelector } from "store";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import {
  amendOfferWithSync,
  emptyOffer,
  getEditOffer,
} from "services/storeFront";
import { useCapContext } from "cap/providers/CapContext";
import { useNavigateHelper } from "utils/useNavigateHelper";
import { ChangeOrderDelayModal } from "components/organisms/ChangeOrderDelayModal";
import { RepRedesignDesignRender } from "components/organisms/RepRedesignDesignRender";
import { getReworkTaskByName, getReworkTaskDetails } from "cap/api/capApi";
import { RepricingLoadingWizard } from "cap/components/organisms/RepricingLoadingWizard";
import { isSignedProposal } from "cap/utils/proposalHelpers";
import { useOfferList } from "checkout/hooks/useCheckoutSwr";
import { getOfferFromProposalId } from "utils/offerListHelpers";
import { clearCapRepRedesignDocRefreshState } from "cap/utils/capRepRedesignDocRefresh";

type RepRedesignStep = "DESIGN" | "PRICING";

const RepRedesignPage = () => {
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const [userType, setUserType] = useState<string>();
  const navigate = useNavigateHelper({ replace: true });
  const [step, setStep] = useState<RepRedesignStep>("DESIGN");
  const [showReprepricingWizard, setShowReprepricingWizard] = useState(false);
  const [lightmileProjectId, setLightmileProjectId] = useState<string>();
  const [designId, setDesignId] = useState<string>();
  const [taskId, setTaskId] = useState<string>();
  const [taskEligible, setTaskEligible] = useState<boolean>(false);
  const [hudEligibilityError, setHudEligibilityError] = useState<Error | null>(
    null
  );
  const [reviewOfferId, setReviewOfferId] = useState<string>();
  const [selectedPricePointId, setSelectedPricePointId] = useState<string>();
  const [submitOfferSuccess, setSubmitOfferSuccess] = useState(false);
  const [submitOfferFailure, setSubmitOfferFailure] = useState<Error | null>(
    null
  );
  const [determinantTaskId, setDeterminantTaskId] = useState<string>();
  const [initialTaskName, setInitialTaskName] = useState<string>();
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [ihdError, setIhdError] = useState<Error | null>(null);
  const [warning, setWarning] = useState<Error | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const {
    activeProposal,
    prospectId,
    sow,
    userProfile,
    originalProposal,
    refetchCapDocuments,
  } = useCapContext();
  const { proposal: sowProposal, ultimateParentProposal } = sow || {};

  const signedProposal = isSignedProposal(
    activeProposal,
    originalProposal,
    ultimateParentProposal
  );

  const { offerList } = useOfferList(prospectId!, cognitoToken);

  const originalDesignCopyId = useMemo(() => {
    if (signedProposal || !originalProposal?.proposalId) {
      return undefined;
    }
    const originalOffer = offerList
      ? getOfferFromProposalId(offerList, originalProposal.proposalId)
      : undefined;
    return originalOffer?.designId || originalProposal.proposalId;
  }, [signedProposal, originalProposal, offerList]);

  const handleDesignComplete = useCallback((completedDesignId: string) => {
    setDesignId(completedDesignId);
    setLoadingPricing(true);
  }, []);

  useEffect(() => {
    if (userProfile) {
      setUserType(getSplatUserType(userProfile));
    }
  }, [userProfile]);

  useEffect(() => {
    if (sow && !activeProposal) {
      setError(new Error("No active proposal present"));
      return;
    }
  }, [activeProposal, sow]);

  useEffect(() => {
    if (!prospectId || !cognitoToken || !activeProposal || !sowProposal) {
      return;
    }

    if (signedProposal) {
      const signedRootProposalId = ultimateParentProposal?.signed
        ? ultimateParentProposal.externalId
        : originalProposal?.proposalId;

      amendOfferWithSync(
        cognitoToken,
        prospectId,
        activeProposal.proposalId,
        signedRootProposalId || activeProposal.proposalId
      )
        .then((amendData) => {
          setReviewOfferId(amendData);
        })
        .catch((err) => {
          setError(err);
        });
      return;
    }

    if (!activeProposal.proposalId) {
      setError(new Error("No parent proposal present on active proposal"));
      return;
    }

    emptyOffer(cognitoToken, prospectId, undefined, activeProposal.proposalId)
      .then((result) => {
        setReviewOfferId(result.id);
      })
      .catch((err) => {
        setError(err);
      });
  }, [
    cognitoToken,
    prospectId,
    activeProposal,
    sowProposal,
    ultimateParentProposal,
    originalProposal,
    signedProposal,
  ]);

  useEffect(() => {
    if (!designId || !reviewOfferId || !cognitoToken) {
      return;
    }
    getEditOffer(cognitoToken, reviewOfferId)
      .then((data) => {
        setLightmileProjectId(data.lightmileProjectId);
        setStep("PRICING");
        setLoadingPricing(false);
      })
      .catch((err) => {
        setLoadingPricing(false);
        setError(err);
      });
  }, [reviewOfferId, designId, cognitoToken]);

  useEffect(() => {
    if (!authKey || !prospectId || !userType || !sow) {
      return;
    }
    const { priceToCustomer } = sow;

    let taskName = "Prepare CAP Package";
    if (
      (userType === DIRECT_TO_HOME ||
        userType === FUSION_CLOSER ||
        userType === FUSION_SETTER) &&
      priceToCustomer > 0
    ) {
      taskName = "Home Upgrades Deduction";
    }
    setInitialTaskName(taskName);
    getReworkTaskByName(authKey, prospectId, taskName)
      .then((data) => {
        const task = data[0];

        if (!task?.taskId) {
          setError(new Error("No task id is present on this task."));
          return;
        }
        setTaskId(task.taskId);
        getReworkTaskDetails(authKey, task.taskId)
          .then((taskDetails) => {
            const { ReworkTaskDetails } = taskDetails;
            const {
              reworkEligible,
              reworkIneligibleReason,
              determinantTaskList,
            } = ReworkTaskDetails;

            if (determinantTaskList) {
              const determinantTaskKey = Object.keys(determinantTaskList)[0];
              setDeterminantTaskId(determinantTaskKey);
            }

            if (reworkEligible) {
              setTaskEligible(true);
            } else if (taskName === "Home Upgrades Deduction") {
              setHudEligibilityError(new Error(reworkIneligibleReason));
            } else {
              setError(
                new Error(
                  `The task '${taskName}' is ineligible for rework: ${reworkIneligibleReason}`
                )
              );
            }
          })
          .catch((err) => {
            setError(err);
          });
      })
      .catch((err) => {
        setError(err);
      });
  }, [authKey, prospectId, sow, userType]);

  const hudEligibilityPrimaryButton = {
    text: "Acknowledge",
    onClick: () => setHudEligibilityError(null),
    testId: "rep-redesign-hud-eligibility-error-modal-acknowledge-button",
    ariaLabel: "rep-redesign-hud-eligibility-error-modal-acknowledge-button",
    disabled: false,
  };

  if (ihdError) {
    return (
      <ErrorModal
        testId="rep-redesign-ihd-error-modal"
        title="Error from another system"
        error={ihdError ?? undefined}
        onClose={() => {
          setIhdError(null);
        }}
      >
        <Typography color={tokens.TINTS_OFF_WHITE_20}>
          ONE received an error from another system. This is not an issue from
          ONE but from another technical system that ONE relies on.
        </Typography>
        <ErrorComponent
          context="RepRedesignPage"
          action="IHD Error"
          error={ihdError ?? undefined}
          errorFromAnotherSystem
        />
      </ErrorModal>
    );
  }
  if (error) {
    return (
      <ErrorModal
        error={error ?? undefined}
        testId="rep-redesign-loading-error-modal"
        onClose={() => {
          setError(null);
        }}
      >
        <ErrorComponent
          context="RepRedesignPage"
          action="Loading Error"
          error={error ?? undefined}
        />
      </ErrorModal>
    );
  }
  if (hudEligibilityError) {
    return (
      <ErrorModal
        testId="rep-redesign-hud-eligibility-error-modal"
        title="You may need to call SSD to reprice this CAP"
        onClose={() => setHudEligibilityError(null)}
        error={hudEligibilityError ?? undefined}
        primaryButton={hudEligibilityPrimaryButton}
      >
        <Grid>
          <Typography>
            The Home Upgrade Deduction task of the WFM project is set to
            inactive, you may not be able to reprice this CAP. If you are able
            to reprice, you may still need to call SSD in order to rework the
            Home Upgrades Deduction task.
          </Typography>
        </Grid>
      </ErrorModal>
    );
  }
  if (warning) {
    return <ChangeOrderDelayModal setWarning={setWarning} />;
  }

  const eligibleToFinish =
    initialTaskName === "Home Upgrades Deduction" || taskEligible;

  const pricingButtons: HeaderButton[] = [
    {
      type: "secondary",
      text: "Back",
      onClick: () => {
        navigate(`/cap`);
      },
    },
    {
      type: "primary",
      text: "Finish",
      disabled:
        !selectedPricePointId ||
        submittingOffer ||
        !lightmileProjectId ||
        !eligibleToFinish,
      onClick: () => {
        setSubmittingOffer(true);
        setShowReprepricingWizard(true);
      },
    },
  ];

  if (step === "DESIGN") {
    const loadingDesign = !reviewOfferId;

    if (loadingDesign || loadingPricing) {
      return (
        <LoadingIndicator
          fullScreen
          color="black"
          message={loadingPricing ? "Getting pricing..." : "Loading design..."}
        />
      );
    }

    return (
      <RepRedesignDesignRender
        offerId={reviewOfferId}
        prospectId={prospectId!}
        onDesignComplete={handleDesignComplete}
        onDesignCancel={() => {
          if (prospectId) {
            clearCapRepRedesignDocRefreshState(prospectId);
          }
          navigate(`/cap`);
        }}
        setError={setIhdError}
        copyDesignOrProposalId={originalDesignCopyId}
        parentProposalId={activeProposal?.proposalId}
      />
    );
  }

  return (
    <>
      {showReprepricingWizard &&
        taskId &&
        initialTaskName &&
        activeProposal?.proposalId && (
          <RepricingLoadingWizard
            refetchCapDocuments={refetchCapDocuments}
            submittingOffer={submittingOffer}
            submitOfferSuccess={submitOfferSuccess}
            submitOfferFailure={submitOfferFailure}
            userId={userProfile?.userId as string}
            determinantTaskId={determinantTaskId}
            initialTaskName={initialTaskName}
            initialTaskId={taskId}
            taskEligible={taskEligible}
            isFromRedesignFlow
          />
        )}
      <HeaderContentLayout
        smallPadding
        description={
          <Typography>
            Select the product and price for your customer&apos;s new CAP
            proposal then click Finish. Follow all on-screen instructions after
            clicking Finish.
          </Typography>
        }
        content={
          !lightmileProjectId || !reviewOfferId ? (
            <div>
              <LoadingIndicator color="black" message="Getting pricing..." />
            </div>
          ) : (
            <PricingPage
              lightmileProjectId={lightmileProjectId}
              repChangeOrder
              offerId={reviewOfferId as string}
              prospectId={prospectId!}
              changeOrder={true}
              offerEvSpan={true}
              onPricePointIdSelected={(pricePointId) => {
                setSelectedPricePointId(pricePointId);
              }}
              onSubmitFailure={(err: Error) => {
                setSubmittingOffer(false);
                setSubmitOfferFailure(err);
              }}
              onSubmitComplete={() => {
                setSubmitOfferSuccess(true);
                setSubmittingOffer(false);
              }}
              submittingOffer={submittingOffer}
              setChangeOrderError={setWarning}
            />
          )
        }
        title="Pricing"
        buttons={pricingButtons}
      />
    </>
  );
};

const Grid = styled.div`
  display: grid;
  gap: 20px;
`;

export { RepRedesignPage };
